import { useEffect, useRef, useState } from 'react';
import { Send, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAssistantSend } from '@/features/assistant/hooks';
import {
  CLAUDE_MODELS,
  getStoredModel,
  setStoredModel,
} from '@/features/assistant/models';
import { cn } from '@/lib/utils';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: number;
  model?: string;
}

interface AssistantPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Right-side slide-over chat with Atlas. Strictly grounded to internal
 * documents that the caller's role allows them to see (RLS enforces it on
 * the Edge Function side).
 */
export function AssistantPanel({ open, onOpenChange }: AssistantPanelProps) {
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [model, setModel] = useState<string>(() => getStoredModel());
  const send = useAssistantSend();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  if (!open) return null;

  const handleModelChange = (next: string) => {
    setModel(next);
    setStoredModel(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || send.isPending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    try {
      const result = await send.mutateAsync({ message: text, conversationId, model });
      setConversationId(result.conversation_id);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: result.answer,
          sources: result.sources,
          model: result.model,
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: `Couldn't reach Atlas: ${(err as Error).message}`,
        },
      ]);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-card shadow-xl">
      <header className="flex h-14 items-center gap-2 border-b border-border px-4">
        <Sparkles className="h-4 w-4 text-old-growth" strokeWidth={1.6} />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-medium text-foreground">Ask Atlas</span>
          <span className="text-[11px] text-muted-foreground">
            Grounded to docs you can see
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(false)}
          className="ml-auto"
          aria-label="Close assistant"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="flex items-center gap-2 border-b border-border px-4 py-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Model
        </span>
        <Select value={model} onValueChange={handleModelChange}>
          <SelectTrigger className="h-8 w-auto min-w-[200px] flex-1 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CLAUDE_MODELS.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{m.label}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {m.description}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
        {messages.map((m, i) => (
          <div
            key={i}
            className={cn(
              'flex w-full',
              m.role === 'user' ? 'justify-end' : 'justify-start',
            )}
          >
            <div
              className={cn(
                'max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-relaxed',
                m.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border bg-secondary/50 text-foreground',
              )}
            >
              {m.content}
              {m.role === 'assistant' && (m.model || typeof m.sources === 'number') && (
                <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {m.model && <span>{labelForModel(m.model)}</span>}
                  {typeof m.sources === 'number' && (
                    <span>
                      · {m.sources} source{m.sources === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        {send.isPending && (
          <div className="text-xs text-muted-foreground">Atlas is thinking…</div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="border-t border-border bg-background p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent);
              }
            }}
            rows={2}
            placeholder="Ask Atlas anything about Virro…"
            className="flex-1 resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button type="submit" size="icon" disabled={!input.trim() || send.isPending}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function labelForModel(id: string): string {
  return CLAUDE_MODELS.find((m) => m.id === id)?.label ?? id;
}
